/** @jsx jsx */
import {
  BinIcon,
  Button,
  FileEntryIconsProvider,
  FileEntryIconsProviderProps,
  FileEntryMode,
  Flex,
  forwardRefWithAs,
  IrisTheme,
  Link,
  Paragraph,
  Status,
} from "@eworkit/iris";
import { useCallback, useMemo } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { unstable_useId as useId } from "reakit/Id";
import { jsx } from "theme-ui";

import { FileUploadStateItem } from "@/components/Files/upload/fileUploadReducer";
import { Attachment } from "@/data/attachments";

export type FileEntryFile = Partial<File> & Pick<File, "name">;
export type FileEntryVariants = keyof IrisTheme["fileUpload"]["variants"];

export interface FileEntryInnerProps {
  onDelete?: (file: FileEntryFile) => void;
  onSetDefault?: (file: FileUploadStateItem<Attachment>) => void;
  variant?: FileEntryVariants;
  mode?: FileEntryMode;
  file?: FileEntryFile;
  IconsProvider?: React.ComponentType<FileEntryIconsProviderProps>;
  ariaLabelDelete: string;
  subtitle?: string;
}

const useStyles = (variant: FileEntryVariants) => {
  return useCallback(
    () => ({
      variant: `fileEntry.variants.${variant.toString()}`,
    }),
    [variant],
  );
};

export const CvFileEntry = forwardRefWithAs<FileEntryInnerProps, "div">(
  (
    {
      onDelete,
      onSetDefault,
      className,
      variant = "primary",
      mode = FileEntryMode.VIEW,
      file,
      IconsProvider = FileEntryIconsProvider,
      ariaLabelDelete,
      subtitle,
      id,
      as: Component = "div",
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation("session");

    const { id: nextId = "" } = useId();
    const selfId = id || nextId;

    const handlers = useMemo(() => {
      return {
        handleDelete: () => {
          if (typeof onDelete === "function" && file) {
            onDelete(file);
          }
        },
        handleDefaultFileButtonClick: () => {
          if (typeof onSetDefault === "function" && file) {
            onSetDefault(file);
          }
        },
      };
    }, [file, onDelete, onSetDefault]);

    const DrawActions = useMemo(() => {
      return (
        <div data-fileentry="actions" data-testid="fileActions">
          <React.Fragment>
            {onDelete && (
              <Button
                as="button"
                type="button"
                display="inline-block"
                iconLeft={<BinIcon />}
                size="sm"
                variant="ghost-danger"
                onClick={handlers.handleDelete}
                data-testid="deleteButton"
                aria-label={ariaLabelDelete}
              />
            )}
          </React.Fragment>
        </div>
      );
    }, [selfId, handlers.handleDelete, handlers.handleDefaultFileButtonClick, onDelete, ariaLabelDelete, onSetDefault]);

    return (
      <div sx={useStyles(variant)}>
        <Component
          className={className}
          ref={ref}
          data-fileentry="component"
          data-testid="fileItem"
          data-mode={mode}
          data-clickable={typeof rest.onClick === "function"}
          data-delete-active={typeof onDelete === "function"}
          id={selfId}
          {...rest}
          sx={
            file.defaultFile || mode === FileEntryMode.EDIT
              ? {
                  '&[data-fileentry="component"]': {
                    pl: "42px",
                    pb: "34px",
                  },
                }
              : undefined
          }>
          <Flex
            alignItems="center"
            sx={
              file.defaultFile || mode === FileEntryMode.EDIT
                ? {
                    position: "absolute",
                    left: "19px",
                    top: 0,
                    bottom: 0,
                  }
                : undefined
            }>
            <IconsProvider data-fileentry="fileIcon" data-testid="fileIcon" color="n700" size="md" name={file?.name} />
          </Flex>
          <div data-fileentry="fileName" data-testid="fileName">
            <div data-fileentry="fileNameText">{file?.name}</div>

            {subtitle && (
              <div data-fileentry="subtitle" sx={{ mt: "xxs" }}>
                {subtitle}
              </div>
            )}
          </div>
        </Component>
        {DrawActions}

        {(file.defaultFile || mode === FileEntryMode.EDIT) && (
          <Flex
            sx={{
              position: "absolute",
              bottom: "12px",
              left: "52px",
            }}>
            {file.defaultFile ? (
              <Status size="sm" variant="primary">
                {t("CONSULTANT_PROFILES.DETAIL.CV.DEFAULT")}
              </Status>
            ) : (
              <Paragraph size="p-sm">
                <Link as="button" onClick={handlers.handleDefaultFileButtonClick}>
                  {t("CONSULTANT_PROFILES.DETAIL.CV.SET_AS_DEFAULT")}
                </Link>
              </Paragraph>
            )}
          </Flex>
        )}
      </div>
    );
  },
);
